import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import { motion } from 'framer-motion'; // Import Framer Motion
import { Shield, Security, Policy } from '@mui/icons-material'; // Import Material UI Icons
import { AttachMoney, CreditCard, TrendingUp, AccountBalance, Home, Apartment } from '@mui/icons-material'; // Importing relevant Material-UI Icons

// The CardWithLeftLine component
const CardWithLeftLine = ({ title, body, bulletPoints, icon }) => {
  // Mapping icon names to corresponding Material-UI icons
  const IconMap = {
    shield: <Shield sx={{ fontSize: 40, color: '#27ae60' }} />,
    security: <Security sx={{ fontSize: 40, color: '#2980b9' }} />,
    policy: <Policy sx={{ fontSize: 40, color: '#8e44ad' }} />,
    loan: <AttachMoney sx={{ fontSize: 40, color: '#2980b9' }} />,
    credit: <CreditCard sx={{ fontSize: 40, color: '#2980b9' }} />,
    investment: <TrendingUp sx={{ fontSize: 40, color: '#27ae60' }} />,
    balance: <AccountBalance sx={{ fontSize: 40, color: '#8e44ad' }} />,
    home: <Home sx={{ fontSize: 40, color: '#f39c12' }} />,
    apartment: <Apartment sx={{ fontSize: 40, color: '#f39c12' }} />,
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}  // Initially invisible
      animate={{ opacity: 1 }}   // Animate to full opacity
      whileInView={{ opacity: 1 }} // Trigger animation when in view
      exit={{ opacity: 0 }}      // Exit animation when out of view (optional)
      transition={{ duration: 1 }} // Transition duration
      viewport={{ once: true }}  // Ensures the animation happens only once when the card enters the view
      whileHover={{ scale: 1.05 }}
    >
      <Card
        sx={{
          maxWidth: 345,
          mb: 4,
          borderRadius: 3, // Rounded corners for a softer look
          boxShadow: 3, // Light shadow to elevate the card
          '&:hover': {
            boxShadow: 6, // Increase shadow on hover
            transform: 'translateY(-5px)', // Slight lift on hover
            transition: 'all 0.3s ease',
          },
        }}
      >
        <CardContent sx={{ paddingBottom: 3 }}>
          {/* Container for title, logo/icon, and left line */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* Left line */}
            <Box
              sx={{
                width: 6, // Thicker line for more visual impact
                height: '32px', // Taller line for a balanced look
                backgroundColor: '#2980b9', // Line color
                mr: 2,
              }}
            />

            {/* Logo/Icon */}
            <Box
              sx={{
                width: '40px', // Set the width of the logo
                height: '40px', // Set the height of the logo
                borderRadius: '50%', // Make the logo circular
                overflow: 'hidden', // Ensure the logo fits inside the circle
                mr: 2, // Space between logo and title
                display: 'flex', // Flex for aligning icons
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {/* Render the passed Material UI icon */}
              {IconMap[icon] || <Shield sx={{ fontSize: 40, color: '#27ae60' }} />}
            </Box>

            {/* Title Text */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600, // Bold title for emphasis
                color: '#333', // Darker color for better readability
              }}
            >
              {title}
            </Typography>
          </Box>

          {/* Body content in the Card */}
          <Typography
            variant="body2"
            color="text.secondary"
            paragraph
            sx={{ fontSize: '1rem', lineHeight: 1.6, color: '#555' }}
          >
            {body}
          </Typography>

          {/* Bullet Points */}
          {bulletPoints && bulletPoints.length > 0 && (
            <ul style={{ paddingLeft: '20px', marginTop: '10px' }}>
              {bulletPoints.map((point, index) => (
                <li key={index} style={{ marginBottom: '8px' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.95rem' }}>
                    {point}
                  </Typography>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default CardWithLeftLine;
